import React, { useContext } from 'react';

import LedgerDiscountTotal from '../../../types/LedgerDiscountTotal';
import LedgerEntry from '../../../types/LedgerEntry';
import LedgerLineItem from '../../../types/LedgerLineItem';
import { getAmountToTransfer } from '../helpers';
import '../../../styles/components/PriceBreakdown.css';
import { convertNumberToUSD } from '../../../infrastructure/utils/common';
import { LedgerContext } from '../../../contexts/LedgerContext';
import LedgerAdditionalCharge from '../../../types/LedgerAdditionalCharge';
import LedgerDiscount from '../../../types/LedgerDiscount';

type Props = {
  entry: LedgerEntry;
};

type LedgerPriceBasedChargeTotal = LedgerDiscountTotal;

function PriceBreakdownLineItem(props: {
  item: LedgerLineItem;
  chargesMap: Map<string, LedgerAdditionalCharge>;
  discount?: LedgerDiscount;
}) {
  const perTicketTotal =
    props.item.price +
    Object.values(props.item.chargesPerTicket).reduce((acc, curr) => {
      return acc + curr;
    }, 0) -
    props.item.discountPerTicket +
    props.item.taxPerTicket;

  return (
    <>
      <p
        className='price-breakdown__line price-breakdown__line--title '
        key={`${props.item.id}-title`}
      >
        <span className='price-breakdown__line-title  price-breakdown__cell--span-5'>
          <b>{props.item.title}</b>
        </span>
      </p>
      <p
        className='price-breakdown__line price-breakdown__line--details'
        key={`${props.item.id}-price`}
      >
        <span className='price-breakdown__line-title'>Per Ticket Price</span>
        <span className='price-breakdown__cell--span-3'>
          {convertNumberToUSD(props.item.price / 100)}
        </span>
      </p>
      {Object.entries(props.item.chargesPerTicket).map(([id, amount]) => {
        const charge = props.chargesMap.get(id);
        if (!charge) return;

        return (
          <p
            className='price-breakdown__line price-breakdown__line--details'
            key={`${props.item.id}-charge-${id}`}
          >
            <span className='price-breakdown__line-title'>
              Per Ticket {charge.title}
            </span>
            <span className='price-breakdown__cell--span-2'>
              {Number(charge.value.toFixed(2))}%
            </span>
            <span>{convertNumberToUSD(amount / 100)}</span>
          </p>
        );
      })}

      {!!props.item.discountPerTicket && !!props.discount && (
        <p
          className='price-breakdown__line price-breakdown__line--details'
          key={`${props.item.id}-discount`}
        >
          <span className='price-breakdown__line-title'>
            Per Ticket Discount
          </span>
          <span className='price-breakdown__cell--span-2'>
            {Number(props.discount.value.toFixed(2))}%
          </span>
          <span>{convertNumberToUSD(-props.item.discountPerTicket / 100)}</span>
        </p>
      )}

      {!!props.item.taxPerTicket && (
        <p
          className='price-breakdown__line price-breakdown__line--details'
          key={`${props.item.id}-tax`}
        >
          <span className='price-breakdown__line-title'>Per Ticket Tax</span>
          <span className='price-breakdown__cell--span-2'>
            {Number(props.item.taxRate.toFixed(2))}%
          </span>
          <span>{convertNumberToUSD(props.item.taxPerTicket / 100)}</span>
        </p>
      )}

      <hr className='price-breakdown__details-divider' />

      <p
        className='price-breakdown__line price-breakdown__line--details'
        key={`${props.item.id}-total`}
      >
        <span className='price-breakdown__line-title'>Per Ticket Total</span>
        <span className='price-breakdown__cell--span-3'>
          {convertNumberToUSD(perTicketTotal / 100)}
        </span>
      </p>

      <p
        className='price-breakdown__line price-breakdown__line--total'
        key={`${props.item.id}-item-total`}
      >
        <span className='price-breakdown__cell--span-5 text-right'>
          <b>
            Total Tickets: {props.item.quantity} x{' '}
            {convertNumberToUSD(perTicketTotal / 100)} ={' '}
            {convertNumberToUSD((perTicketTotal * props.item.quantity) / 100)}
          </b>
        </span>
      </p>
    </>
  );
}

export default function PriceBreakdown({ entry }: Props) {
  const ledgerContext = useContext(LedgerContext);
  const chargesMap = new Map<string, LedgerAdditionalCharge>(
    (ledgerContext.additionalCharges || []).map(ch => [ch.id, ch])
  );

  const hasAnyPriceBasedCharges =
    entry.totalCharges.length > 0 ||
    entry.totalDiscount ||
    entry.totalTaxAmount !== 0;
  const needToShowTransferBreakdown =
    entry.totalPlatformProcessingFee.amount !== 0 ||
    entry.totalTransferredToVendor.taxAmount !== 0;

  const renderCharge = (
    charge: LedgerPriceBasedChargeTotal,
    skipPercentage: boolean = false,
    key: string | undefined = undefined
  ) => {
    return (
      <p className='price-breakdown__line' key={key}>
        <span className='price-breakdown__line-title'>{charge.title}</span>
        <span className='price-breakdown__cell--span-2'>
          {charge.valueType === 'percentage'
            ? skipPercentage
              ? ''
              : `${Number(charge.value.toFixed(2))}%`
            : convertNumberToUSD(charge.value / 100)}
        </span>
        <span>{convertNumberToUSD(charge.amount / 100)}</span>
      </p>
    );
  };

  const totalTransferAmount = getAmountToTransfer(entry);

  return (
    <div className='price-breakdown'>
      {entry.lineItems.map(item => (
        <PriceBreakdownLineItem
          key={item.id}
          item={item}
          chargesMap={chargesMap}
          discount={ledgerContext.discount}
        />
      ))}

      <hr />

      {hasAnyPriceBasedCharges && (
        <>
          <p className='price-breakdown__line'>
            <span className='price-breakdown__line-title'>Subtotal</span>
            <span className='price-breakdown__cell--span-3'>
              {convertNumberToUSD(entry.subTotal / 100)}
            </span>
          </p>
          {entry.totalCharges.map(ch => renderCharge(ch, true, ch.id))}
          {entry.totalDiscount &&
            renderCharge(
              {
                ...entry.totalDiscount,

                title: `Discount '${entry.totalDiscount.title}'`,
              },
              true
            )}
          {entry.totalTaxAmount !== 0 &&
            renderCharge(
              {
                title: 'Tax',
                value: entry.avgTaxRate,
                valueType: 'percentage',
                amount: entry.totalTaxAmount,
              },
              true
            )}

          <hr />
        </>
      )}

      <p className='price-breakdown__line'>
        <span className='price-breakdown__line-title'>
          <b>Total {entry.totalAmount >= 0 ? 'Charged' : 'Refunded'}</b>
        </span>
        <span className='price-breakdown__cell--span-3'>
          {convertNumberToUSD(entry.totalAmount / 100)}
        </span>
      </p>

      <hr />

      {entry.totalCommission !== 0 && (
        <>
          {renderCharge({
            title: 'Commission',
            value: entry.avgCommission,
            valueType: 'percentage',
            amount: entry.totalCommission,
          })}

          <hr />
        </>
      )}

      {needToShowTransferBreakdown && (
        <>
          <p className='price-breakdown__line'>
            <span className='price-breakdown__line-title'>
              {entry.totalTransferredToVendor.amount >= 0
                ? 'Transferred To Vendor'
                : 'Reversed From Vendor'}
            </span>
            <span className='price-breakdown__cell--span-3'>
              {convertNumberToUSD(entry.totalTransferredToVendor.amount / 100)}
            </span>
          </p>
          {entry.totalTransferredToVendor.taxAmount !== 0 &&
            renderCharge({
              title: `Tax ${
                entry.totalTransferredToVendor.taxAmount >= 0
                  ? 'Transferred To'
                  : 'Reversed From'
              } Vendor`,
              value: entry.avgTaxRate,
              valueType: 'percentage',
              amount: entry.totalTransferredToVendor.taxAmount,
            })}
          {entry.totalPlatformProcessingFee.amount !== 0 &&
            renderCharge({
              title: 'Platform Processing Fee',
              value: entry.totalPlatformProcessingFee.rate,
              valueType:
                entry.totalPlatformProcessingFee.type === 'percentage'
                  ? 'percentage'
                  : 'fixed_amount',
              amount: entry.totalPlatformProcessingFee.amount,
            })}

          <hr />
        </>
      )}

      <p className='price-breakdown__line'>
        <span className='price-breakdown__line-title price-breakdown__cell--span-4'>
          <b>
            {totalTransferAmount >= 0
              ? 'Total Transferred To Vendor'
              : 'Total Reversed From Vendor'}
          </b>
        </span>
        <span className='price-breakdown__cell--span-1'>
          {convertNumberToUSD(totalTransferAmount / 100)}
        </span>
      </p>
    </div>
  );
}
